import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Descriptions, Space, Table, Tabs } from 'antd';
import dayjs from 'dayjs';
import client from '../../api/client';
import { pageLabels as pages, eventLabels as events } from './chartData';

const AnalyticsCharts = lazy(() => import('./AnalyticsCharts'));
const countColumns = [{ title: '已授权登录账号', dataIndex: 'loggedUsers' }, { title: '已授权匿名安装', dataIndex: 'anonymousInstalls' }];
const retention = (value, row) => value == null ? '尚未到观察日结束' : `${value} / ${row.newSubjects}（${row.newSubjects ? (value * 100 / row.newSubjects).toFixed(1) : 0}%）`;

export default function Analytics() {
  const [range, setRange] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const from = range?.[0]?.format('YYYY-MM-DD');
  const to = range?.[1]?.format('YYYY-MM-DD');
  useEffect(() => {
    let current = true;
    let running = false;
    const controller = new AbortController();
    setReport(null); setUpdatedAt(null); setError(false);
    const fetchReport = async (background = false) => {
      if (running) return;
      running = true;
      if (!background) setLoading(true);
      try {
        const data = await client.get('/analytics', { params: { from, to }, signal: controller.signal, silentError: true });
        if (current) { setReport(data); setError(false); setUpdatedAt(new Date()); }
      } catch (failure) {
        if (current) setError(true);
      } finally {
        running = false;
        if (current) setLoading(false);
      }
    };
    fetchReport();
    const onVisible = () => { if (!document.hidden) fetchReport(true); };
    const timer = window.setInterval(onVisible, 5000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      current = false; controller.abort(); window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [from, to, refresh]);
  const table = (rows, columns, key) => <Table rowKey={key} dataSource={rows || []} columns={columns} loading={loading} size="small" pagination={{ pageSize: 15 }} />;
  return <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
    <h2>使用统计</h2>
    <Alert type="info" showIcon title="仅统计已同意隐私政策的用户样本"
      description="用户同意隐私政策后默认参与统计。撤回隐私同意的用户不计入本报表，因此不能代表全量日活。登录账号按账号去重；匿名按授权期间的随机安装标识去重，两者不可相加为人数。数据保留30天，时区为 Asia/Shanghai，今天的数据尚未完整。" />
    <Space wrap>
      <DatePicker.RangePicker value={range} onChange={setRange} placeholder={['开始日期（默认近7天）', '结束日期']}
        disabledDate={(date) => date.isBefore(dayjs().subtract(29, 'day'), 'day') || date.isAfter(dayjs(), 'day')} />
      <Button onClick={() => setRefresh((n) => n + 1)} loading={loading}>刷新</Button>
      <span>每5秒自动刷新{updatedAt ? ` · 更新于 ${dayjs(updatedAt).format('HH:mm:ss')}` : ''}</span>
      {report?.from && report?.to && <span>报告日期：{report.from} 至 {report.to}</span>}
    </Space>
    {error && <Alert type="error" title="统计加载失败，请刷新重试" />}
    <Card title="截至所选结束日的活跃" loading={loading}>
      <Descriptions column={3}>
        {[['days1', '日活 DAU'], ['days7', '周活 WAU'], ['days30', '月活 MAU']].map(([key, label]) =>
          <Descriptions.Item key={key} label={label}>{report?.active?.[key]
            ? `${report.active[key].loggedUsers} 个账号 / ${report.active[key].anonymousInstalls} 个匿名安装` : '完整窗口数据不可用'}</Descriptions.Item>)}
      </Descriptions>
      <span style={{ color: '#666' }}>活跃以 APP 前台活动计算；账号变化会更换统计标识，重新授权的匿名安装无法跨授权周期去重。</span>
    </Card>
    <Card size="small" title="所选日期内的数据接收情况">
      <Descriptions column={3}>
        <Descriptions.Item label="记录总数">{report?.summary?.eventCount ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="打开 / 回前台次数">{report?.summary?.foregroundEvents ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="最近事件">{report?.summary?.latestEventAt ? dayjs(report.summary.latestEventAt).format('MM-DD HH:mm:ss') : '暂无'}</Descriptions.Item>
      </Descriptions>
      {report && !Number(report.summary?.eventCount || 0) && <p>尚未收到记录。请确认手机已更新，并完成现有隐私协议流程后打开 APP，再刷新本页。旧版默认关闭，历史“使用情况”页不展示新的客户端埋点。</p>}
    </Card>
    <Tabs items={[
      { key: 'charts', label: '概览图表', children: <Suspense fallback={<div>图表加载中…</div>}><AnalyticsCharts report={report} /></Suspense> },
      { key: 'daily', label: '每日活跃', children: table(report?.daily, [{ title: '日期', dataIndex: 'day' }, ...countColumns], 'day') },
      { key: 'retention', label: '新观测与留存', children: <>
        <p>新观测指该统计主体首次出现前台活动，不等于新增注册。次日/7日留存按第1/7个自然日是否活跃计算；观察日结束后才展示。删除数据可能使历史结果下降。</p>
        {table(report?.retention, [{ title: '首次观测日期', dataIndex: 'day' }, { title: '主体类型', dataIndex: 'kind', render: (v) => v === 'LOGGED' ? '登录账号' : '匿名安装' }, { title: '新观测数', dataIndex: 'newSubjects' }, { title: '次日留存', dataIndex: 'retained1', render: retention }, { title: '7日留存', dataIndex: 'retained7', render: retention }], (r) => `${r.day}-${r.kind}`)}
      </> },
      { key: 'features', label: '功能使用', children: table(report?.features, [{ title: '页面', dataIndex: 'pageName', render: (v) => pages[v] || v }, { title: '事件', dataIndex: 'eventName', render: (v) => events[v] || v }, { title: '次数', dataIndex: 'eventCount' }, ...countColumns], (r) => `${r.pageName}-${r.eventName}`) },
      { key: 'funnel', label: '商品漏斗', children: <><p>所选日期内，同一授权安装依次发生主动搜索 → 商品详情浏览 → 请求购买链接。购买链接请求不代表成交，跨安装行为不拼接。</p>{table(report?.funnel, [{ title: '主体类型', dataIndex: 'kind', render: (v) => v === 'LOGGED' ? '登录账号' : '匿名安装' }, { title: '搜索', dataIndex: 'searched' }, { title: '商品详情', dataIndex: 'viewed' }, { title: '购买链接', dataIndex: 'buyLink' }], 'kind')}</> },
      { key: 'versions', label: '版本分布', children: table(report?.versions, [{ title: '版本', dataIndex: 'label' }, ...countColumns], 'label') },
      { key: 'channels', label: '渠道分布', children: table(report?.channels, [{ title: '渠道', dataIndex: 'label' }, ...countColumns], 'label') },
    ]} />
  </Space>;
}
